/**
 * ModernSidebar - Sidebar Component for Modern Layout
 * 
 * Uses the shared UI component library Sidebar.
 */

import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { Sidebar } from '@crc-evans/ui-components';
import {
  LayoutDashboard,
  FolderOpen,
  Briefcase,
  Wrench,
  ClipboardCheck,
} from 'lucide-react';
import './ModernSidebar.css';

const ModernSidebar = (props) => {
  const history = useHistory();
  const location = useLocation();
  // Technical routes: highlight Technical when on any of these
  const technicalPaths = ['/heat-profile', '/DCALGenerator', '/TechnicalPage', '/technical'];
  const calibrationPaths = ['/calibration'];

  // Determine initial active state based on current path
  const [activeId, setActiveId] = useState(() => {
    const path = window.location.pathname;
    if (technicalPaths.some((p) => path === p || path.startsWith(p + '/'))) return 'technical';
    if (calibrationPaths.some((p) => path === p || path.startsWith(p + '/'))) return 'calibration';
    if (path === '/projects' || path.startsWith('/projects/') || path === '/dpr' || path.startsWith('/dpr')) {
      return 'projects';
    }
    return 'dashboard';
  });

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userGroup = JSON.parse(localStorage.getItem('UserGroup') || '[]');

  // Map user data for Sidebar component
  const defaultUser = {
    name: user['custom:first_name'] && user['custom:last_name']
      ? `${user['custom:first_name']} ${user['custom:last_name']}`
      : user['custom:first_name'] || 'User',
    role: (userGroup?.includes('super-admin') || userGroup?.includes('admin')) ? 'Administrator' : 'User',
    initials: (
      (user['custom:first_name']?.charAt(0) || '') +
      (user['custom:last_name']?.charAt(0) || '')
    ).toUpperCase() || 'U'
  };

  // Update active state based on current route
  useEffect(() => {
    const path = location.pathname;
    if (technicalPaths.some((p) => path === p || path.startsWith(p + '/'))) {
      setActiveId('technical');
    } else if (calibrationPaths.some((p) => path === p || path.startsWith(p + '/'))) {
      setActiveId('calibration');
    } else if (path === '/projects' || path.startsWith('/projects/') || path === '/dpr' || path.startsWith('/dpr')) {
      setActiveId('projects');
    } else if (path === '/admin' || path.startsWith('/admin/')) {
      setActiveId('admin');
    } else {
      setActiveId('dashboard');
    }
  }, [location.pathname]);

  const handleItemClick = (itemId) => {
    if (itemId === 'projects') {
      setActiveId('projects');
      history.push('/projects');
    } else if (itemId === 'technical') {
      setActiveId('technical');
      history.push('/heat-profile');
    } else if (itemId === 'calibration') {
      setActiveId('calibration');
      history.push('/calibration');
    } else if (itemId === 'admin') {
      const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      window.open(
        `${window.location.origin}${base}/admin/asset-management`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const items = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
    },
    {
      id: 'projects',
      label: 'Projects',
      icon: FolderOpen,
    },
  ];

  if (
    userGroup?.includes('heat-profile') ||
    userGroup?.includes('coating-record-db') ||
    userGroup?.includes('dcal-generator') ||
    userGroup?.includes('super-admin') ||
    userGroup?.includes('admin')
  ) {
    items.push({
      id: 'technical',
      label: 'Technical',
      icon: Wrench,
    });
  }

  if (
    userGroup?.includes('heat-profile') ||
    userGroup?.includes('coating-record-db') ||
    userGroup?.includes('dcal-generator') ||
    userGroup?.includes('super-admin') ||
    userGroup?.includes('admin')
  ) {
    items.push({
      id: 'calibration',
      label: 'Calibration',
      icon: ClipboardCheck,
    });
  }

  if (userGroup?.includes('admin') || userGroup?.includes('super-admin')) {
    items.push({
      id: 'admin',
      label: 'Admin',
      icon: Briefcase,
    });
  }

  return (
    <Sidebar
      items={items}
      activeItemId={activeId}
      onItemClick={handleItemClick}
      user={defaultUser}
      className="modern-sidebar-override"
    />
  );
};

const mapStateToProps = ({ auth }) => ({
  userAccess: auth?.userAccess || [],
});

export default connect(mapStateToProps)(ModernSidebar);
